import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  ToolboxItemProps,
  ToolboxItemType,
  Container,
  SwitchChainAlert,
} from '@occc-app/design-system';
import config from '@occc/config';
import { useChainId } from '@occc-app/Chain/hooks';
import { messages as offerMessages } from '@occc-app/routes/Offer/messages';
import { messages as punchcardMessages } from '@occc-app/routes/Punchcard/messages';
import { messages as challengeMessages } from '@occc-app/routes/Challenge/messages';
import { messages as promotionMessages } from '@occc-app/routes/Promotion/messages';
import { messages as rewardMessages } from '@occc-app/routes/Reward/messages';
import { messages as sweepstakesMessages } from '@occc-app/routes/Sweepstakes/messages';
import { messages as billboardMessages } from '@occc-app/routes/Billboard/messages';
import { messages as gameMessages } from '@occc-app/routes/Game/messages';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { messages as promocodesMessages } from '@occc/feat-promocodes';
import { useCampaignDeleteMutation } from '@occc-app/dataAccessLayer/campaign/mutations';
import { useGetCampaignById } from '@occc-app/dataAccessLayer/campaign/queries';

import { CampaignDetailsPage } from './CampaignDetailsPage';

import { routeCreator } from '../utils';

export const getToolboxItems = (): ToolboxItemProps[] => {
  const toolboxItems: ToolboxItemProps[] = [
    {
      title: 'Offer',
      description: offerMessages.subtitle,
      type: ToolboxItemType.Offer,
      linkToCreate: 'offer/create',
    },
    {
      title: 'Reward',
      description: rewardMessages.subtitle,
      type: ToolboxItemType.Reward,
      linkToCreate: 'reward/create',
    },
    {
      title: 'Promotion',
      description: promotionMessages.subtitle,
      type: ToolboxItemType.Promotion,
      linkToCreate: 'promotion/create',
    },
    {
      title: 'Challenge',
      description: challengeMessages.subtitle,
      type: ToolboxItemType.Challenge,
      linkToCreate: 'challenge/create',
    },
    {
      title: 'Club or Punchcard',
      description: punchcardMessages.subtitle,
      type: ToolboxItemType.Punchcard,
      // TODO: replace with routeCreator
      linkToCreate: 'punchcard/create',
    },
    {
      title: 'Sweepstakes',
      description: sweepstakesMessages.subtitle,
      type: ToolboxItemType.Sweepstakes,
      linkToCreate: 'sweepstakes/create',
    },
    {
      title: 'Billboards',
      description: billboardMessages.subtitle,
      type: ToolboxItemType.Billboards,
      linkToCreate: 'billboard/create',
    },
    {
      title: 'Games',
      description: gameMessages.subtitle,
      type: ToolboxItemType.FreeGames,
      linkToCreate: 'freegame/create',
    },
  ];

  if (config.features.promocodesOffersEnabled) {
    toolboxItems.push({
      title: 'Promo Code Offer',
      description: promocodesMessages.subtitle,
      type: ToolboxItemType.Promocodes,
      linkToCreate: 'promocodes/create',
    });
  }

  return toolboxItems;
};

export const CampaignDetailsPageContainer: React.FC = () => {
  const { id: campaignId = '' } = useParams<{ id: string }>();

  const chainId = useChainId();
  const navigate = useNavigate();
  const { loading, error, data: campaign } = useGetCampaignById(campaignId);
  const [campaignDelete] = useCampaignDeleteMutation();

  const isCampaignFromDifferentChain = campaign && campaign.chain.id !== chainId;
  const toolboxItems = getToolboxItems();

  const handleCampaignDelete = () => {
    campaignDelete({
      variables: {
        uuid: campaignId,
      },
    });
    navigate(routeCreator.list());
  };

  if (isCampaignFromDifferentChain) {
    return (
      <Container maxWidth='1024px'>
        <SwitchChainAlert
          title='This campaign is not available in your currently selected chain.'
          requiredChainId={campaign?.chain.id || ''}
        />
      </Container>
    );
  }

  return (
    <CampaignDetailsPage
      loading={loading}
      error={error}
      campaign={campaign}
      toolboxItems={toolboxItems}
      handleCampaignDelete={handleCampaignDelete}
    />
  );
};
